<template>
  <div
    :id="id"
    v-tooltip.top="tooltip"
    :aria-describedby="helpTextId"
    class="consolidated-shipment-dial flex flex-column align-items-center"
    tabindex="-1"
  >
    <small :id="helpTextId" class="visually-hidden" tabindex="-1">
      {{ tooltip }}
    </small>

    <Knob :model-value="value" :class="status" :max="effectiveMax" readonly />
    <div class="consolidated-shipment-dial-uom">{{ uomLabel }}</div>
    <div class="consolidated-shipment-dial-label">{{ label }}</div>
  </div>
</template>

<script lang="ts" setup>
import { usePrimeVue } from 'primevue/config';
import { computed, toRefs } from 'vue';

import { useTooltip } from '@rygen/rygen-ui/composables';

import Knob from 'primevue/knob';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  value: {
    type: Number,
    default: () => 0,
  },
  label: {
    type: String,
    default: () => '',
  },
  uomLabel: {
    type: String,
    default: () => '',
  },
  max: {
    type: Number,
    required: true,
  },
  tooltip: {
    type: String,
    required: true,
  },
});
const { id, value, label, uomLabel, max, tooltip } = toRefs(props);

const vTooltip = useTooltip();
defineExpose({
  $primevue: usePrimeVue(),
});

const helpTextId = computed(() => `${id.value}-help`);

/**
 * The maximum value that the knob represents
 *
 * This is not the same as the maximum value that is allowed. The user could
 * potentially go over the maximum allowed value, so the knob has to be able
 * to display that.
 */
const effectiveMax = computed(() => {
  return max?.value !== undefined
    ? Math.max(max.value, value.value)
    : value.value;
});

const status = computed(() => {
  if (value.value > max.value) {
    return 'danger';
  } else if (value.value > 0.8 * max.value) {
    return 'warning';
  } else {
    return 'success';
  }
});
</script>

<style>
.consolidated-shipment-dial {
  --p-knob-range-background: var(--p-surface-200);

  .p-knob {
    &.success {
      --p-knob-value-background: var(--success-color);
    }

    &.warning {
      --p-knob-value-background: var(--warning-color);
    }

    &.danger {
      --p-knob-value-background: var(--danger-color);
    }
  }
}
</style>
